import React, { useEffect } from "react";
import { Provider, useSelector } from "react-redux";
import { store, persistor } from "./redux/store";
import { PersistGate } from "redux-persist/integration/react";
import AppRouter from "./router/AppRouter";
import { SnackbarProvider } from "notistack";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n"; // Import the initialized i18n instance
import { SubscriptionProvider } from "./contexts/SubscriptionProvider";
import { ThemeProvider as CustomThemeProvider } from "./contexts/ThemeContext";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ZoomProvider } from "./contexts/ZoomProvider";
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import eventSourceManager from "./utils/eventSourceManager";
import createAppTheme from "./theme/theme";
import AnalyticsGate from "./analytics/AnalyticsGate";
import './App.css';

/**
 * Inner App Component
 * Uses theme from Redux to create MUI theme
 */
const AppContent = () => {
  const themeMode = useSelector((state) => state.theme?.mode || 'light');

  // Cleanup EventSource connections on app unmount
  useEffect(() => {
    return () => {
      console.log('App unmounting, closing all EventSource connections');
      eventSourceManager.closeAllConnections();
    };
  }, []);

  return (
    <MuiThemeProvider theme={createAppTheme(themeMode)}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SnackbarProvider
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <SubscriptionProvider>
            <I18nextProvider i18n={i18n}>
              <AppRouter />
              <AnalyticsGate />
            </I18nextProvider>
          </SubscriptionProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </MuiThemeProvider>
  );
};

/**
 * Main App Component
 * Wraps everything with providers
 */
const App = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <CustomThemeProvider>
          <AppContent />
        </CustomThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
